import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { SourceOfWealthOption } from "../../../../../../../Wrap/Constants/Enums/Apply/SourceOfWealthOption";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { Urls } from "../../../../../Urls";
import {
    CedingTypeMap,
    SourceOfWealthArraryMap,
    ThirdPartyTypeArrayMap,
    TransferTypeMap,
} from "../../../NewBusinessWizard/Resource/Contents";
import {
    depositMethodArraysMap,
    DpcQuestionText,
    PaymentType,
    regularDepositMethodArraysMap,
    TransferInModelMap,
} from "../../Resource/Contents";
import {
    boolParse,
    moneyParse,
    regularPaymentParse,
    singlePaymentParse,
    transferInParse,
    bankAccountParse,
    pensionArrangementTypeParse,
    IReviewProps,
} from "../ReviewParser";
import { NewQuoteAction } from "../../../NewQuoteWizard/Actions/NewQuoteAction";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { QuoteAndApplyBaseModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/QuoteAndApplyBaseModel";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { getMoneyFormat } from "../../../NewQuoteWizard/Components/Shared/Utils";
import { ThirdPartyPaymentModel } from "../../../../../../../Wrap/Models/Models/Shared/ThirdPartyPaymentModel";
import { TitleMap } from "../../../../../../Shared/Entities/Maps/TitleMap";

export const PaymentsInReview = (props: IReviewProps) => {
    const { data, extraInfo, subAccountType, process } = props;

    const singlePaymentTypeConfig = (paymentData: any, paymentType: PaymentType) => {
        if (!paymentData) {return false;}
        return { title: `${paymentType} single payment`, data: singlePaymentParse(paymentData, subAccountType) };
    };

    const bankAccountConfig = (bankAccountNumber: any) => {
        if (!bankAccountNumber) {return false;}

        return { title: "Bank account", data: bankAccountParse(bankAccountNumber, extraInfo) };
    };

    const paymentMethodConfig = (paymentMethod: any, isRegular: boolean) => {
        if (!paymentMethod) {return false;}
        return {
            title: "Payment method",
            data: isRegular ? regularDepositMethodArraysMap[paymentMethod] : depositMethodArraysMap[paymentMethod],
        };
    };

    const paymentStartConfig = (model: any) => {
        if (!model) {return false;}
        return {
            title: "Collection starts",
            data: JsonDateParser.getFormatDateString(
                new Date(model?.RegularBuyId ? model?.NextPaymentDate : model?.FirstCollectionDate),
                "dd MM yyyy",
                true
            ),
        };
    };

    const regularPaymentTypeConfig = (paymentData: any, paymentType: PaymentType) => {
        if (!paymentData) {return false;}
        return { title: `${paymentType} regular payment`, data: regularPaymentParse(paymentData, subAccountType) };
    };

    const ThirdPartyConfig = (thirdPartyPaymentModel: ThirdPartyPaymentModel) => {
        if (!thirdPartyPaymentModel) {return false;}
        return [
            { title: "Third Party Type", data: ThirdPartyTypeArrayMap[thirdPartyPaymentModel.Type] },
            {
                title: "Client Name",
                data: `${TitleMap.getString(thirdPartyPaymentModel.Title)} ${thirdPartyPaymentModel.FirstName} ${
                    thirdPartyPaymentModel.LastName
                }`,
            },
            {
                title: "Date Of Birth",
                data: JsonDateParser.getFormatDate(thirdPartyPaymentModel.DateOfBirth, "dd/MM/yyyy"),
            },
            { title: "Address Line 1", data: thirdPartyPaymentModel.Address?.Line1 },
            { title: "Address Line 2", data: thirdPartyPaymentModel.Address?.Line2 },
            { title: "Address Line 3", data: thirdPartyPaymentModel.Address?.Line3 },
            { title: "Address Line 4", data: thirdPartyPaymentModel.Address?.Line4 },
            { title: "PostCode", data: thirdPartyPaymentModel.Address?.Postcode },
        ];
    };

    const isAssumedShow = (): boolean => {
        if (process != ProcessType.Quote || !extraInfo) {return false;}
        switch (subAccountType) {
            case SubAccountType.Npr:
            case SubAccountType.Sipp:
                return true;
            case SubAccountType.StocksIsa:
                return !(extraInfo as QuoteAndApplyBaseModel).SelectedProducts.some((ele) =>
                    SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
                );

            case SubAccountType.InvestmentAccount:
                return (
                    !(extraInfo as QuoteAndApplyBaseModel).SelectedProducts.some((ele) =>
                        SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
                    ) &&
                    !(extraInfo as QuoteAndApplyBaseModel).SelectedProducts.some((ele) =>
                        SubAccountTypeHelper.isIsa(ele.SubAccountType)
                    )
                );
            default:
                return false;
        }
    };
    const getRetireAge = (model: QuoteAndApplyBaseModel): string => {
        const birthday = JsonDateParser.toDateNoOffset(model?.ClientDetails?.DateOfBirth);
        const plannedRetirementDate = JsonDateParser.toDateNoOffset(model?.AnnuityAssumptions?.PlannedRetirementDate);
        return plannedRetirementDate
            ? (plannedRetirementDate.getFullYear() - birthday.getFullYear()).toString()
            : "None";
    };

    const getAssumeAmount = (model: QuoteAndApplyBaseModel): string => {
        const Assumed = model?.AssumedHoldings;

        return Assumed == null ? "None" : moneyParse(model?.AssumedHoldings?.Amount);
    };

    const paymentsInfoConfig = (model: QuoteAndApplyBaseModel) => {
        if (!extraInfo) {return [];}
        const config = [
            process != ProcessType.SinglePayment &&
                process != ProcessType.TopUpWithITFLS &&
                SubAccountTypeHelper.isOnPlatformPension(subAccountType) && {
                    title: "Selected retirement",
                    data: getRetireAge(model),
                },
            isAssumedShow() && {
                title: "Assumed other holdings",
                data: getAssumeAmount(model),
            },
        ];
        return config.filter(Boolean);
    };

    const singlePaymentConfig = () => {
        let config = [];
        config.push(singlePaymentTypeConfig(data.ClientSinglePayment, PaymentType.Client));
        if (shouldDisplaySinglePaymentDetail()) {
            config.push(paymentMethodConfig(data.ClientSinglePayment?.PaymentMethod, false));
            config.push(bankAccountConfig(data.ClientSinglePayment?.BankAccountNumber));
        }

        config.push(singlePaymentTypeConfig(data.ThirdPartySinglePayment, PaymentType.ThirdParty));
        if (shouldDisplaySinglePaymentDetail()) {
            config.push(paymentMethodConfig(data.ThirdPartySinglePayment?.PaymentMethod, false));
            config.push(bankAccountConfig(data.ThirdPartySinglePayment?.BankAccountNumber));
            config = config.concat(ThirdPartyConfig(data.ThirdPartySinglePayment));
        }

        config.push(singlePaymentTypeConfig(data.EmployerSinglePayment, PaymentType.Employer));
        if (shouldDisplaySinglePaymentDetail()) {
            config.push(paymentMethodConfig(data.EmployerSinglePayment?.PaymentMethod, false));
            config.push(bankAccountConfig(data.EmployerSinglePayment?.BankAccountNumber));
        }

        return config.filter(Boolean);
    };

    const regularPaymentConfig = () => {
        let config = [];
        config.push(regularPaymentTypeConfig(data.ClientRegularPayment, PaymentType.Client));
        if (shouldDisplayRegularPaymentDetail()) {
            config.push(paymentStartConfig(data?.ClientRegularPayment));
            config.push(paymentMethodConfig(data.ClientRegularPayment?.RegularPaymentMethod, true));
            config.push(bankAccountConfig(data.ClientRegularPayment?.BankAccountNumber));
        }

        config.push(regularPaymentTypeConfig(data.ThirdPartyRegularPayment, PaymentType.ThirdParty));
        if (shouldDisplayRegularPaymentDetail()) {
            config.push(paymentStartConfig(data?.ThirdPartyRegularPayment));
            config.push(paymentMethodConfig(data.ThirdPartyRegularPayment?.RegularPaymentMethod, true));
            config.push(bankAccountConfig(data.ThirdPartyRegularPayment?.BankAccountNumber));
            config = config.concat(ThirdPartyConfig(data.ThirdPartyRegularPayment));
        }

        config.push(regularPaymentTypeConfig(data.EmployerRegularPayment, PaymentType.Employer));
        if (shouldDisplayRegularPaymentDetail()) {
            config.push(paymentStartConfig(data?.EmployerRegularPayment));
            config.push(paymentMethodConfig(data.EmployerRegularPayment?.RegularPaymentMethod, true));
            config.push(bankAccountConfig(data.EmployerRegularPayment?.BankAccountNumber));
        }

        return config.filter(Boolean);
    };

    const shouldDisplaySinglePaymentDetail = () => {
        return (
            process == ProcessType.NB || process == ProcessType.SinglePayment || process == ProcessType.TopUpWithITFLS
        );
    };

    const shouldDisplayRegularPaymentDetail = () => {
        return process == ProcessType.NB || process == ProcessType.EditRegular || process == ProcessType.AddRegular;
    };

    const transferInConfig = () => {
        const config = [];
        const addExtraInfo =
            shouldDisplaySinglePaymentDetail() && SubAccountTypeHelper.isOnPlatformPension(subAccountType);
        const transferInArr = transferInParse(data, subAccountType);
        const transferins = data[TransferInModelMap[subAccountType]];
        !isNullOrEmpty(transferInArr) &&
            transferInArr.map((ele, index) => {
                const transferin = transferins[index];
                if (process == ProcessType.TopUpWithITFLS) {
                    const transferin = data[TransferInModelMap[subAccountType]];
                    transferin.map((ele, index) => {
                        config.push({ title: "Transfer In " + (index + 1), data: " " });

                        config.push({
                            title: "Pension source",
                            data: pensionArrangementTypeParse(ele?.PensionSource),
                        });

                        config.push({
                            title: "Ceding scheme",
                            data: ele.ProviderDetails?.Name,
                        });
                        config.push({
                            title: "Ceding type",
                            data: CedingTypeMap[ele.PensionDetails?.ExternalPensionSchemeType],
                        });
                        config.push({
                            title: "Block transfer",
                            data: boolParse(ele.Protection?.BlockTransferProtection?.HasProtection),
                        });
                        config.push({
                            title: "Dependant's drawdown",
                            data: boolParse(ele?.PensionDetails?.IsDependentsPension),
                        });
                        config.push({
                            title: "Account number",
                            data: ele.AccountOrPlanNumber,
                        });
                        config.push({
                            title: "Transfer type",
                            data: TransferTypeMap[ele.TransferType],
                        });
                        config.push({
                            title: "Current value",
                            data: getMoneyFormat(ele.CurrentValue.Amount),
                        });
                        config.push({
                            title: "Full or partial transfer",
                            data: ele.FullTransfer ? "Full" : "Partial",
                        });
                    });
                } else {
                    config.push({ title: "Transfer In " + (index + 1), data: ele });
                    if (addExtraInfo) {
                        config.push({
                            title: "Pension source",
                            data: pensionArrangementTypeParse(transferin?.PensionSource),
                        });
                        config.push({
                            title: "Dependant's pension",
                            data: boolParse(transferin?.PensionDetails?.IsDependentsPension),
                        });
                        config.push({
                            title: DpcQuestionText,
                            data: boolParse(transferin?.PensionDetails?.IsDisqualifyingPensionCredits),
                        });
                    }
                }
            });
        return config;
    };

    const extraInfoConfig = () => {
        const config = [];
        if (!extraInfo) {return config;}
        if (process == ProcessType.NB && SubAccountTypeHelper.isOnPlatformPension(subAccountType)) {
            config.push({
                title: "Source of wealth",
                data: SourceOfWealthArraryMap[data?.SourceOfWealth?.SourceOfWealthOption],
            });
            if (data?.SourceOfWealth?.SourceOfWealthOption == SourceOfWealthOption.Other) {
                config.push({
                    title: "Other Source of wealth",
                    data: data.SourceOfWealth.OtherSourceOfWealth,
                });
            }
        }
        return config;
    };

    const navigate = () => {
        if (process == ProcessType.NB) {
            Navigator.navigateToAndPreserveQueryParameters(paymentsInUrlConfig());
        } else if (process == ProcessType.Quote) {
            paymentsInUrlConfigForNewQuote();
        } else if (process == ProcessType.SinglePayment) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().SPTPaymentsIn);
        } else if (process == ProcessType.TopUpWithITFLS) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().TopUpWithITFLSPaymentsIn);
        } else if (process == ProcessType.AddRegular || ProcessType.EditRegular) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().RegularPaymentsIn);
        }
    };

    const paymentsInUrlConfigForNewQuote = () => {
        switch (subAccountType) {
            case SubAccountType.InvestmentAccount:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuotePaymentsInPP);
            case SubAccountType.StocksIsa:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuotePaymentsInISA);
            case SubAccountType.Pension:
            case SubAccountType.Sipp:
            case SubAccountType.Npr:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuotePaymentsInSIPP);
            default:
                break;
        }
    };
    const paymentsInUrlConfig = () => {
        switch (subAccountType) {
            case SubAccountType.InvestmentAccount:
                return Urls.get().NBPaymentsInPP;
            case SubAccountType.WrapCash:
                return Urls.get().NBPaymentsInWrapCash;
            case SubAccountType.StocksIsa:
                return Urls.get().NBPaymentsInISA;
            case SubAccountType.Pension:
            case SubAccountType.Sipp:
            case SubAccountType.Npr:
                return Urls.get().NBPaymentsInSIPP;
            default:
                break;
        }
    };

    return (
        <>
            {
                <Accordion
                    label="Payments in"
                    isOpen={props.isOpen != null ? props.isOpen : false}
                    onEdit={() => (props.onEditAction ? props.onEditAction() : navigate())}
                >
                    <div className="pl-34">
                        <Typography type="common" config={paymentsInfoConfig(extraInfo)} colNum={1}></Typography>
                        <Typography type="common" config={singlePaymentConfig()} colNum={1}></Typography>
                        {(shouldDisplayRegularPaymentDetail() || props.process == ProcessType.Quote) && (
                            <Typography type="common" config={regularPaymentConfig()} colNum={1}></Typography>
                        )}
                        <Typography type="common" config={transferInConfig()} colNum={1}></Typography>
                        {SubAccountTypeHelper.isOnPlatformPension(subAccountType) && (
                            <Typography type="common" config={extraInfoConfig()} colNum={1}></Typography>
                        )}
                    </div>
                </Accordion>
            }
        </>
    );
};
